import {
  AgGridManager,
  GridColumnData,
  GridFilterTypes,
  GridRenderSlots
} from "@digitallab/grid-common-lib";
import { prepareLogbookEquipmentSchema } from "../logbookEquipmentSchema";
import { makeSchemaClient } from "./schemaClient";

/**
 * The per-field flag lists `constants.tsx` used to maintain by hand, derived from the schema instead.
 * Names match the constants they replace, minus the `listOf` prefix.
 *
 * Only the lists whose content the schema can express are here. Two are absent on purpose:
 *   listOfFieldsSortable       feeds the persisted `sortStatus` and nothing else — see its comment
 *                              in `constants.tsx`.
 *   listOfFieldsIgnoredInTable shapes `DEFAULT_FILTER`, not the grid.
 *
 * `fieldAcceptOnlyInteger` has no counterpart in booking-frontend's version of this file; logbook
 * needs it because `listOfFieldAcceptOnlyInteger` gates its number inputs.
 */
export interface GridConstants {
  fieldsNotSortable: string[];
  dateRequired: string[];
  booleanRequired: string[];
  checkBoxFilters: string[];
  numberFilter: string[];
  fieldAcceptOnlyInteger: string[];
  fieldsNoFilter: string[];
  filterBlankRequired: string[];
  fieldsIgnoredForSearch: string[];
}

export const deriveGridConstants = (columns: GridColumnData[]): GridConstants => ({
  fieldsNotSortable: columns.filter((column) => !column.sortStatus).map((column) => column.key),
  dateRequired: columns
    .filter((column) => column.filter === GridFilterTypes.DATE)
    .map((column) => column.key),
  // Yes/no columns, which take a two-value set filter rather than the master-data one.
  booleanRequired: columns
    .filter((column) => column.renderSlot === GridRenderSlots.BOOLEAN)
    .map((column) => column.key),
  // Set filters excluding the booleans, which `getColumnObj` handles on its own branch.
  checkBoxFilters: columns
    .filter(
      (column) =>
        column.filter === GridFilterTypes.SET && column.renderSlot !== GridRenderSlots.BOOLEAN
    )
    .map((column) => column.key),
  numberFilter: columns
    .filter((column) => column.filter === GridFilterTypes.NUMBER)
    .map((column) => column.key),
  fieldAcceptOnlyInteger: columns.filter((column) => column.integerOnly).map((column) => column.key),
  fieldsNoFilter: columns
    .filter((column) => column.filter === GridFilterTypes.NONE)
    .map((column) => column.key),
  filterBlankRequired: columns.filter((column) => column.filterBlank).map((column) => column.key),
  fieldsIgnoredForSearch: columns
    .filter((column) => column.ignoredForSearch)
    .map((column) => column.key)
});

/**
 * Schema-derived grid column data, cached for the session.
 *
 * A module-level cache rather than a hook because the `listOf…` constants are synchronous and are
 * read from places that are not components — `EquipmentHelper.getColumnObj` among them. Preparing
 * the schema is asynchronous, so it resolves once through {@link loadEquipmentGridColumns} and is
 * read synchronously thereafter.
 *
 * Until it resolves, the getters below answer `null` and the constants fall back to their
 * hand-written originals, so a screen rendering early keeps today's behaviour rather than seeing
 * empty lists.
 */
let columnDataCache: GridColumnData[] | null = null;
let gridConstantsCache: GridConstants | null = null;
let inFlight: Promise<GridColumnData[]> | null = null;

/**
 * Prepare the logbook equipment schema and cache the derived flag lists.
 *
 * Safe to call repeatedly — concurrent callers share one in-flight request and later calls resolve
 * from the cache. Call it before an equipment grid renders.
 *
 * @returns The derived column data.
 */
export const loadEquipmentGridColumns = async (): Promise<GridColumnData[]> => {
  if (columnDataCache) {
    return columnDataCache;
  }
  if (inFlight) {
    return inFlight;
  }

  inFlight = prepareLogbookEquipmentSchema(makeSchemaClient())
    .then(async (schema) => {
      const manager = new AgGridManager(schema);
      // Resolves the grid.filterValues getters — set-filter options for columns the form drives
      // through a picker or free text, which are therefore not data enums.
      await manager.init();

      const columns = manager.generateColumnData();
      columnDataCache = columns;
      gridConstantsCache = deriveGridConstants(columns);
      return columns;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
};

/**
 * The derived flag lists, or `null` before {@link loadEquipmentGridColumns} resolves.
 *
 * Imported by `constants.tsx`. The AppSync client stays behind {@link makeSchemaClient}, which jest
 * replaces through `moduleNameMapper` — so importing this module from `constants.tsx` does not drag
 * `aws-appsync` into every test that reaches the constants.
 */
export const getGridConstants = (): GridConstants | null => gridConstantsCache;

/** Cached column data, or `null` when the schema has not resolved yet. */
export const getEquipmentGridColumns = (): GridColumnData[] | null => columnDataCache;

/** Test seam: drop the cached schema so each test starts from a known state. */
export const resetEquipmentGridColumns = (): void => {
  columnDataCache = null;
  gridConstantsCache = null;
  inFlight = null;
};
